import
{
    useL10n
} from "@ews/react-localization-context";

import
{
    FC, useState
} from "react";

import
{
    Sidebar,
    MenuLink,
    SidebarSection,
    MenuLinkProps
} from "./Sidebar";

import type {
    MenuToggleProps
} from "../types";

import
{
    IonItem,
    IonLabel
} from "@ionic/react";

import
{
    SystemElement,
    SystemGroup,
    useOperateSystem
} from "../../ReactData/system";

import
{
    Command,
    //Command,
    Commands,
    getTranslationKey,
    groupOperate,
    // groupTypeToNumberGroup,
    // type GroupTypes
} from '@ews/zlt-operate';

import { AuthorizationLevel, NoDeviceNumber, NumberGroup, formatLogicalAddress } from "@ews/zlt-events";
import { useAuthorization } from "../../Authorization";
import { ReactClickEvent } from "../../types";
import { SystemOperate, SystemRTM } from "@ews/react-data";
import { useIsMobile } from "../../Responsive";
import { Locale } from "../../../setup/locale";
import { useMyProfile } from "../../Login/LoginProvider";

export type OperateTarget = (SystemGroup | SystemElement);

export type SelectRange = {
    begin?: OperateTarget,
    end?: OperateTarget;
};

type CustomLink = {
    link: string;
    title: Locale;
};

export type OperateSidebarProps = MenuToggleProps & {
    target: OperateTarget;
    range?: SelectRange;
    onRangeSelect?: (target: OperateTarget) => SelectRange;
    customLinks?: MenuLinkProps[];
    onlyOriginalEvents?: boolean;
};

type OperationItemProps = {
    title: string,
    disabled?: boolean;
    onClick?: (e: ReactClickEvent) => void;
};

const OperationItem: FC<OperationItemProps> = ({
    title,
    disabled = false,
    onClick
}) =>
{
    const { translate: t } = useL10n();

    return (<IonItem disabled={disabled} detail={false} onClick={onClick} button={true} >
        <IonLabel>{t(title)}</IonLabel>

    </IonItem>);
};

export const getNumberGroup = (target: OperateTarget): NumberGroup =>
{
    return target.numberGroup!;
};

export const getAddress = (target: OperateTarget) =>
{
    if ("groupId" in target) {
        return {
            zone: target.groupId!,
            element: target.id!,
            numberGroup: target.numberGroup!
        };
    }
    return {
        zone: target.id!,
        element: NoDeviceNumber,
        numberGroup: target.numberGroup!
    };
};

export const getSystemId = (target: OperateTarget) =>
{
    return target.systemId;
};

export const validOperations = (authorizationLevel: AuthorizationLevel, begin: OperateTarget, end?: OperateTarget, onlyOriginalEvents: boolean = false) =>
{
    const operations: Set<string> = new Set<string>();

    for (const target of [begin, end]) {

        if (target) {

            const numberGroup = getNumberGroup(target);

            const address = getAddress(target);
            const properties = target.properties1 || 0;
            const rtm2 = target.rtm?.filter((v: SystemRTM) => (!onlyOriginalEvents || (v.zone === address.zone && v.element === address.element && v.numberGroup === address.numberGroup))) || [];
            const rtm = rtm2.map((v: SystemRTM) => v.eventKindType!);

            for (const command of Commands) {
                if (groupOperate(numberGroup, command, properties, authorizationLevel, address, rtm)) {
                    operations.add(command);
                }
            }

        }

    }

    return Array.from(operations);
};

const Operate: FC<OperateSidebarProps> = ({
    onMenuToggle,
    target,
    range,
    onRangeSelect,
    customLinks,
    onlyOriginalEvents = false
}) =>
{
    const { authorizationLevel } = useAuthorization();
    const mobileView = useIsMobile();
    const operate = useOperateSystem();
    const { translate: t } = useL10n();
    const [currentRange, setRange] = useState<SelectRange>({ ...range });
    const me = useMyProfile();

    const formatRange = (begin: OperateTarget, end: OperateTarget) =>
    {

        const from = getAddress(begin);
        const to = getAddress(end);

        return `${formatLogicalAddress(from.zone, from.element, from.numberGroup)} - ${formatLogicalAddress(to.zone, to.element, to.numberGroup)}`;
    };

    const rangeTitle = currentRange?.begin ? (currentRange?.end ? formatRange(currentRange.begin, currentRange.end) : '[REACT_OPER_RANGE_END]') : '[REACT_OPER_RANGE_BEGIN]';
    const rangeDisabled = Boolean(currentRange?.begin && currentRange?.end);

    const operations = validOperations(authorizationLevel, currentRange.begin || target, currentRange.end, onlyOriginalEvents);

    const handleRangeSelect = () =>
    {
        if (onRangeSelect) {
            const range = onRangeSelect(target);
            setRange(range);
        }
    };

    const handleOperate = async (command: string, e: ReactClickEvent) =>
    {
        const begin = currentRange.begin || target;
        const end = currentRange.end || begin;

        const systemId = getSystemId(begin);

        const from = getAddress(begin);
        const to = getAddress(end);

        //const type = getType(begin);
        const numberGroup = getNumberGroup(begin);

        await operate(systemId!, {
            command,
            numberGroup,
            firstZone: from.zone,
            lastZone: to.zone,
            firstElement: from.element,
            lastElement: to.element,
        } as SystemOperate);

        onMenuToggle && onMenuToggle(e);

    };

    return (
        <Sidebar onMenuToggle={onMenuToggle}>
            <SidebarSection
                grow
                halfHeight={mobileView}
            >
                {operations.map((operation, index) =>
                    <OperationItem key={index} onClick={(e) => handleOperate(operation, e)} title={t(getTranslationKey(operation as Command))} />
                )}
                {range && <OperationItem disabled={rangeDisabled} onClick={handleRangeSelect} title={t(rangeTitle)} />}
                {customLinks?.map((props, index) => <MenuLink key={index} {...props} />)}

            </SidebarSection>
        </Sidebar>
    );

};

export default Operate;
